import { ArrowRight, Hourglass, Repeat2, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
import { AwardsFeature } from "@/components/site/AwardsFeature";
import { FaqList, ProcessList } from "@/components/site/ContentBlocks";
import {
  AfterBankruptcySupport,
  FearRelief,
  GuaranteeFeature,
  PriceClarity,
} from "@/components/site/ConversionBlocks";
import { EvidenceSection } from "@/components/site/EvidenceSection";
import { FinalContactBlock } from "@/components/site/FinalContactBlock";
import { OfficeExplorer } from "@/components/site/OfficeExplorer";
import { QualificationTest } from "@/components/site/QualificationTest";
import { nonFitItems } from "@/lib/site-data";

const decisionPaths = [
  {
    title: "Nic nie robię",
    icon: Hourglass,
    className: "decision-path-risk",
    items: [
      "Odsetki i koszty windykacji rosną co miesiąc",
      "Wierzyciele kierują sprawy do sądu i komornika",
      "Komornik może zająć część pensji, emerytury lub konto",
    ],
  },
  {
    title: "Spłacam dalej sam",
    icon: Repeat2,
    className: "decision-path-neutral",
    items: [
      "Duża część raty idzie na odsetki, a nie na dług",
      "Nowe pożyczki na spłatę starych tylko powiększają problem",
      "Trudno powiedzieć, kiedy to się skończy",
    ],
  },
  {
    title: "Upadłość konsumencka",
    icon: ShieldCheck,
    className: "decision-path-positive",
    badge: "Tę drogę wybrało już 5 789 Klientów",
    items: [
      "Po ogłoszeniu upadłości egzekucje komornicze zostają z mocy prawa zawieszone, a potem umorzone",
      "Spłacasz tylko tyle, na ile realnie Cię stać - albo sąd umarza długi bez planu spłaty",
      "Na końcu: nowy start bez dawnych długów",
    ],
  },
];

export default function HomeBelowFold() {
  return (
    <>
      <GuaranteeFeature />
      <AwardsFeature />
      <AfterBankruptcySupport />
      <FearRelief />
      <section className="inertia-section section-pad">
        <div className="shell-wide">
          <div className="section-lead">
            <span className="section-kicker warm-kicker">ważna decyzja</span>
            <h2>
              Spłacasz, a długu nie ubywa? Zobacz, dokąd prowadzi każda droga
            </h2>
          </div>
          <div className="decision-path-grid">
            {decisionPaths.map(
              ({ title, icon: Icon, className, badge, items }) => (
                <article
                  className={`decision-path-card ${className}`}
                  key={title}
                >
                  {badge && (
                    <span className="decision-path-badge">{badge}</span>
                  )}
                  <Icon size={30} aria-hidden="true" />
                  <h3>{title}</h3>
                  <ul>
                    {items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  {title === "Upadłość konsumencka" && (
                    <Link className="button-primary" href="#test">
                      Sprawdź, czy to droga dla Ciebie <ArrowRight size={18} />
                    </Link>
                  )}
                </article>
              )
            )}
          </div>
          <p className="table-disclaimer">
            O ogłoszeniu upadłości i jej skutkach decyduje sąd. Niektóre
            zobowiązania (np. alimenty) nie podlegają umorzeniu.
          </p>
          <a className="text-link" href="#postanowienia-sadow">
            Zobacz prawdziwe postanowienia z naszych spraw ↓
          </a>
        </div>
      </section>
      <EvidenceSection />
      <QualificationTest />
      <OfficeExplorer />
      <section id="jak-to-dziala" className="process-section section-pad">
        <div className="shell-narrow">
          <div className="section-lead">
            <span className="section-kicker">jasny proces</span>
            <h2>Jak wygląda prowadzenie sprawy</h2>
            <p>
              Na każdym etapie wiesz, co robimy, ile może potrwać i czego
              potrzebujemy od Ciebie.
            </p>
          </div>
          <ProcessList />
        </div>
      </section>
      <section id="kiedy-nie" className="nonfit-section section-pad">
        <div className="shell-wide nonfit-layout">
          <div>
            <span className="section-kicker">uczciwa kwalifikacja</span>
            <h2>Kiedy ta droga nie będzie najlepsza</h2>
            <p>
              Najlepsza rozmowa nie zawsze kończy się ofertą. Jeśli lepsza
              będzie inna droga, powiemy to wprost.
            </p>
          </div>
          <div className="nonfit-list">
            {nonFitItems.map(item => (
              <article key={item.title}>
                <ShieldCheck size={19} />
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section id="faq" className="faq-section section-pad">
        <div className="shell-wide faq-layout">
          <div className="sticky-aside">
            <span className="section-kicker">
              pytania, które słyszymy najczęściej
            </span>
            <h2>Pytania i odpowiedzi</h2>
            <p>
              Jeśli jedna z odpowiedzi nie wystarczy, możesz zadzwonić lub
              napisać.
            </p>
            <Link className="text-link" href="/faq">
              Zobacz wszystkie pytania <ArrowRight size={17} />
            </Link>
          </div>
          <FaqList limit={10} />
        </div>
      </section>
      <PriceClarity />
      <FinalContactBlock />
    </>
  );
}
